<template>
  <div class="AiRichText">
    <rich-text-renderer
      v-if="document"
      :document="document"
      :node-renderers="NODE_RENDERERS"
      :mark-renderers="MARKS_RENDERERS" />
  </div>
</template>

<script setup lang="ts">
import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import ContentfulRichTextType from '@contentful/rich-text-types';
import RichTextRenderer from 'contentful-rich-text-vue-renderer';
import type { DefineComponent } from 'vue';

import AiTypo from '../../atoms/AiTypo/AiTypo.vue';
import AiLink from '../../atoms/AiLink/AiLink.vue';

const { BLOCKS, MARKS, INLINES } = ContentfulRichTextType;

type Props = {
  document?: RichTextDocument;
};

defineProps<Props>();

const DEFAULT_TYPO = 'paragraph-03-regular';

const MARKS_RENDERERS = {
  [MARKS.BOLD]: (text: string, key: string) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: 'paragraph-03-bold', as: 'span' },
      () => text,
    ),
  [MARKS.ITALIC]: (text: string, key: string) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: 'paragraph-03-italic', as: 'span' },
      () => text,
    ),
  [MARKS.UNDERLINE]: (text: string, key: string) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: DEFAULT_TYPO, as: 'span', underline: true },
      () => text,
    ),
};

const NODE_RENDERERS = {
  [INLINES.HYPERLINK]: (node: any, key: string, next: any) =>
    h(AiLink, { url: node.data.uri }, next(node.content, key, next)),
  [BLOCKS.PARAGRAPH]: (node: any, key: string, next: any) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: DEFAULT_TYPO, as: 'p' },
      next(node.content, key, next),
    ),
  [BLOCKS.HEADING_3]: (node: any, key: string, next: any) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: 'heading-03', as: 'h3' },
      next(node.content, key, next),
    ),
  [BLOCKS.HEADING_4]: (node: any, key: string, next: any) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: 'heading-04', as: 'h4' },
      next(node.content, key, next),
    ),
  [BLOCKS.HEADING_5]: (node: any, key: string, next: any) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: 'heading-05', as: 'h5' },
      next(node.content, key, next),
    ),
  [BLOCKS.HEADING_6]: (node: any, key: string, next: any) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: 'heading-06', as: 'h6' },
      next(node.content, key, next),
    ),
  [BLOCKS.LIST_ITEM]: (node: any, key: string, next: any) =>
    h(
      AiTypo as unknown as DefineComponent<any>,
      { key, variant: DEFAULT_TYPO, as: 'li' },
      next(node.content, key, next),
    ),
};
</script>

<style lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';

.AiRichText {
  ul {
    list-style-type: disc;

    li {
      margin-left: 1rem;

      &::marker {
        color: colors.$gold-900;
      }
    }
  }

  ol {
    list-style-type: decimal;

    li {
      margin-left: 1rem;

      &::marker {
        color: colors.$gold-900;
      }
    }

    ol {
      list-style-type: upper-alpha;

      ol {
        list-style-type: lower-roman;

        ol {
          list-style-type: lower-alpha;
        }
      }
    }
  }

  li > p {
    margin-bottom: constants.$margin-00;
  }
}
</style>
